import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BuildingOfficeIcon, AcademicCapIcon, UserGroupIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import StatCard from '../../components/StatCard';
import api from '../../utils/api';
import CurrencyConverter from '../../components/CurrencyConverter';

const C = { teal:'#00B4D8', navy:'#0D1B2A', success:'#10B981', warning:'#F59E0B', error:'#EF4444', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB' };

export default function SuperAdminOverview() {
  const [data, setData] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [dash, tenantsRes] = await Promise.all([
          api.get('/api/admin/dashboard'),
          api.get('/api/superadmin/tenants'),
        ]);
        setData(dash.data);
        setTenants(tenantsRes.data?.tenants || []);
      } catch (e) {
        setError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const activePartners = tenants.filter(t => (t.status || 'active') === 'active').length;
  const totalStudents = data?.total_students || 0;
  const totalTutors = data?.total_tutors || 0;
  const mrr = tenants.filter(t => t.is_active).reduce((sum, t) => sum + (t.monthly_fee_usd || 0), 0);
  const apiCost = data?.api_cost || 0;

  // Recent tenants sorted by created_at
  const recentTenants = [...tenants]
    .filter(t => t.created_at)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  function timeAgo(isoStr) {
    if (!isoStr) return '';
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    return `${days}d ago`;
  }

  return (
    <Layout role="superadmin">
      {error && (
        <div style={{ background: '#FEE2E2', color: C.error, padding: '12px 16px', borderRadius: 8, marginBottom: 20 }}>{error}</div>
      )}

      {/* Stat Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:20, marginBottom:24 }}>
        <StatCard title="Active Partners" value={loading ? null : activePartners} icon={BuildingOfficeIcon} color={C.teal} loading={loading} />
        <StatCard title="Total Students" value={loading ? null : totalStudents} icon={AcademicCapIcon} color={C.success} loading={loading} />
        <StatCard title="Total Tutors" value={loading ? null : totalTutors} icon={UserGroupIcon} color="#3B82F6" loading={loading} />
        <StatCard title="MRR (run-rate)" value={loading ? null : mrr} icon={CurrencyDollarIcon} color={C.success} prefix="$" loading={loading} />
      </div>

      {/* Currency converter */}
      <div style={{ marginBottom:24 }}>
        <CurrencyConverter accent={C.teal} />
      </div>

      {/* Collected Revenue by period */}
      <div className="card" style={{ padding:24, marginBottom:24 }}>
        <h3 style={{ margin:'0 0 4px', fontSize:15, fontWeight:700 }}>Collected Revenue</h3>
        <p style={{ margin:'0 0 18px', fontSize:13, color:C.grey }}>Confirmed partner subscription payments. MRR above is contracted run-rate; this is money actually received.</p>
        {(() => {
          const rev = data?.revenue || {};
          const periods = [
            ['This month', rev.this_month],
            ['Last month', rev.last_month],
            ['This year',  rev.this_year],
            ['Last year',  rev.last_year],
            ['All time',   rev.all_time],
          ];
          return (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:16 }}>
              {periods.map(([label, value]) => (
                <div key={label} style={{ border:`1px solid ${C.border}`, borderRadius:10, padding:'14px 16px', background: label==='All time' ? '#F0FDF4' : '#fff' }}>
                  <div style={{ fontSize:12, color:C.grey, fontWeight:600, marginBottom:6 }}>{label}</div>
                  <div style={{ fontSize: label==='All time' ? 22 : 18, fontWeight:800, color: (value||0) > 0 ? C.success : C.grey }}>
                    {loading ? '…' : `$${Number(value || 0).toLocaleString()}`}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* API Cost + Activity Feed */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
        {/* API Cost */}
        <div className="card" style={{ padding:24 }}>
          <h3 style={{ margin:'0 0 4px', fontSize:15, fontWeight:700 }}>API Cost Monitor</h3>
          <p style={{ margin:'0 0 16px', fontSize:13, color:C.grey }}>Platform AI spend this month</p>
          {loading ? (
            <div style={{ color: C.grey, fontSize: 14 }}>Loading...</div>
          ) : (
            <>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
                <div style={{ fontSize:32, fontWeight:800, color:C.dark }}>${apiCost.toFixed(2)}</div>
                <div className="badge" style={{ background:'#D1FAE5', color:'#065F46', display:'flex', alignItems:'center', gap:4, padding:'4px 10px', borderRadius:20, fontSize:12, fontWeight:600 }}>
                  Under Budget
                </div>
              </div>
              <div style={{ fontSize:13, color:C.grey }}>Budget: $50.00/day</div>
            </>
          )}
        </div>

        {/* Recent Activity */}
        <div className="card" style={{ padding:24 }}>
          <h3 style={{ margin:'0 0 16px', fontSize:15, fontWeight:700 }}>Recent Partners</h3>
          {loading ? (
            <div style={{ color: C.grey, fontSize: 14 }}>Loading...</div>
          ) : recentTenants.length === 0 ? (
            <div style={{ color: C.grey, fontSize: 14, textAlign: 'center', padding: '20px 0' }}>No partners yet.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {recentTenants.map((t) => (
                <div key={t.id} style={{ display:'flex', alignItems:'flex-start', gap:10, paddingBottom:10, borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ width:32, height:32, borderRadius:8, background:C.teal+'18', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:14 }}>
                    🏫
                  </div>
                  <div style={{ flex:1 }}>
                    <p style={{ margin:'0 0 2px', fontSize:13, color:C.dark, fontWeight:600 }}>{t.name}</p>
                    <span style={{ fontSize:11, color:C.grey }}>{t.plan} · {timeAgo(t.created_at)}</span>
                  </div>
                  <span style={{
                    fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20,
                    background: (t.status||'active')==='active' ? '#D1FAE5' : '#FEF3C7',
                    color: (t.status||'active')==='active' ? C.success : C.warning
                  }}>{t.status || 'active'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Partner Table */}
      <div className="card" style={{ overflow:'hidden' }}>
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>Partner Overview</h3>
          <Link to="/superadmin/tenants" style={{ color:C.teal, fontSize:13, fontWeight:600, textDecoration:'none' }}>View All →</Link>
        </div>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: C.grey }}>Loading...</div>
        ) : tenants.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: C.grey }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🏫</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>No partners yet</div>
            <div style={{ fontSize: 13 }}>
              <Link to="/superadmin/tenants" style={{ color: C.teal, fontWeight: 600, textDecoration: 'none' }}>Create your first partner →</Link>
            </div>
          </div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#F9FAFB' }}>
                {['Partner Name','Plan','City','Students','Monthly Fee','Status'].map(h => (
                  <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:600, color:C.grey, textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tenants.slice(0, 10).map((t) => (
                <tr key={t.id} style={{ borderTop:`1px solid ${C.border}` }}>
                  <td style={{ padding:'14px 16px', fontWeight:600, fontSize:14 }}>{t.name}</td>
                  <td style={{ padding:'14px 16px', fontSize:13, color:C.grey, textTransform:'capitalize' }}>{t.plan || '—'}</td>
                  <td style={{ padding:'14px 16px', fontSize:13, color:C.grey }}>{t.city || '—'}</td>
                  <td style={{ padding:'14px 16px', fontSize:14 }}>{t.student_count ?? 0}</td>
                  <td style={{ padding:'14px 16px', fontSize:14, fontWeight:600, color:C.success }}>${(t.monthly_fee_usd || 0).toFixed(0)}/mo</td>
                  <td style={{ padding:'14px 16px' }}>
                    <span style={{
                      fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:20,
                      background: (t.status||'active')==='active' ? '#D1FAE5' : '#FEF3C7',
                      color: (t.status||'active')==='active' ? C.success : C.warning
                    }}>{t.status || 'active'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
